import React, { createContext, useContext, useState, useCallback } from 'react';

const STORAGE_KEY = 'cookie_consent_preferences';
const CONSENT_VERSION = 1;

const CookieConsentContext = createContext(null);

function getStoredConsent() {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (!stored) return null;
		const parsed = JSON.parse(stored);
		if (parsed.version !== CONSENT_VERSION) return null;
		return parsed;
	} catch {
		return null;
	}
}

function saveConsent(consent) {
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify({
			analytics: consent.analytics,
			marketing: consent.marketing,
			timestamp: new Date().toISOString(),
			version: CONSENT_VERSION,
		}));
	} catch {
		// localStorage not available
	}
}

export function CookieConsentProvider({ children }) {
	const stored = getStoredConsent();

	const [consent, setConsent] = useState({
		necessary: true,
		analytics: stored ? stored.analytics : false,
		marketing: stored ? stored.marketing : false,
	});
	const [consentGiven, setConsentGiven] = useState(!!stored);
	const [showBanner, setShowBanner] = useState(!stored);
	const [showPreferences, setShowPreferences] = useState(false);

	const acceptAll = useCallback(() => {
		const newConsent = { necessary: true, analytics: true, marketing: true };
		setConsent(newConsent);
		setConsentGiven(true);
		setShowBanner(false);
		setShowPreferences(false);
		saveConsent(newConsent);
	}, []);

	const rejectAll = useCallback(() => {
		const newConsent = { necessary: true, analytics: false, marketing: false };
		setConsent(newConsent);
		setConsentGiven(true);
		setShowBanner(false);
		setShowPreferences(false);
		saveConsent(newConsent);
	}, []);

	const savePreferences = useCallback((newConsent) => {
		const updated = { necessary: true, ...newConsent };
		setConsent(updated);
		setConsentGiven(true);
		setShowBanner(false);
		setShowPreferences(false);
		saveConsent(updated);

		// If consent was revoked for a category that was previously active, reload to clear scripts
		const stored = getStoredConsent();
		if (stored) {
			if ((stored.analytics && !updated.analytics) || (stored.marketing && !updated.marketing)) {
				saveConsent(updated);
				window.location.reload();
				return;
			}
		}
	}, []);

	return (
		<CookieConsentContext.Provider value={{
			consent,
			consentGiven,
			showBanner,
			setShowBanner,
			showPreferences,
			setShowPreferences,
			acceptAll,
			rejectAll,
			savePreferences,
		}}>
			{children}
		</CookieConsentContext.Provider>
	);
}

export function useCookieConsent() {
	const context = useContext(CookieConsentContext);
	if (!context) {
		throw new Error('useCookieConsent must be used within CookieConsentProvider');
	}
	return context;
}
